import axios from '../../common/utils/interceptor';
import DeadlineDate from './DeadlineDate';
import React from 'react';
import TaskTile from './TaskTile';
import { apiUrls, WorkflowItemCode } from '../../constants';
import { AxiosResponse } from 'axios';
import { FeedbackPageConfig } from '../../candidateFeedback/interfaces/feedbackpage';
import { Grid, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { Task, TaskItem, TaskListStyleConfig } from '../interfaces';
import { TaskItemStatus } from '../constants';
import { TaskListStyles } from '../styles';
import { useScrollToTaskListLabel } from '../hooks';

interface TaskTilesSectionProps {
  sequential: boolean;
  styles: TaskListStyleConfig;
  tasks: Task[];
  showTileBanner: boolean;
  feedbackPage?: FeedbackPageConfig;
  handleAdvancedRRBtnClick?: (showPopup: boolean) => void;
}

const TaskTilesSection = (props: TaskTilesSectionProps): JSX.Element => {
  const {
    sequential,
    styles: { label, tiles },
    tasks,
    showTileBanner,
    handleAdvancedRRBtnClick,
  } = props;
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const noOfItemsInARow = lgUp ? 3 : smUp ? 2 : 1;

  const classes = TaskListStyles({ ...props.styles });
  const hasAnyTaskWithDescription = tasks.some((task: Task) => task.description);
  const lastSequentiallyActiveTaskIndex = tasks.findIndex(
    (task: Task) => (!task.status || task.status === TaskItemStatus.IN_PROGRESS) && !task.optional,
  );
  const lastPendingTaskIndex = tasks.findIndex((task: Task) => task.status === TaskItemStatus.EVALUATION_PENDING);

  /** Scroll to Task list label on return from any task item */
  const taskListLabelRef = useScrollToTaskListLabel(tasks);

  const fetchAssessmentLaunguages = async (currentItem: TaskItem): Promise<AxiosResponse> => {
    try {
      return await axios.get(`${apiUrls.GET_ASSESSMENT_DETAIL}/${currentItem.id}`);
    } catch (e) {
      console.error('Fetch assessment languages error: ', e);
      return e;
    }
  };

  return (
    <>
      {label.length > 0 && (
        <Grid item xs={12} className={classes.taskListLabelContainer} ref={taskListLabelRef}>
          <Typography variant="h1" className={classes.taskListLabel} title={label}>
            {label}
          </Typography>
        </Grid>
      )}
      <DeadlineDate styles={props.styles} />
      <Grid item className={classes.tileGrid}>
        {tasks.map((task: Task, index: number) => {
          if (!task.visibility) return <></>;

          const doesTaskContainUnscheduledAssessment = task.items.some(
            (item) => item.code === WorkflowItemCode.EXPASSES && item.isSchedulable && !item.scheduled,
          );

          // Check whether current task row contains any scheduled assessment
          const rowNo = Math.floor(index / noOfItemsInARow);
          const rowStartIndex = rowNo * noOfItemsInARow;
          let rowContainsScheduledAssessment = false;
          for (let i = rowStartIndex; i < rowStartIndex + noOfItemsInARow; i++) {
            if (tasks[i]) {
              rowContainsScheduledAssessment = !!tasks[i].isSchedulable;
              if (rowContainsScheduledAssessment) break;
            }
          }
          // Lock tile for not started mandatory tasks and for assessment containing completed tasks
          const isSequentiallyLocked =
            !task.optional &&
            lastSequentiallyActiveTaskIndex >= 0 &&
            index > lastSequentiallyActiveTaskIndex &&
            sequential;
          const isLocked = isSequentiallyLocked || (lastPendingTaskIndex >= 0 && index >= lastPendingTaskIndex);
          const isTaskLocked =
            isLocked ||
            (task.status === TaskItemStatus.COMPLETED && task.containAssessmentItem) ||
            doesTaskContainUnscheduledAssessment;
          return (
            <TaskTile
              key={task.id}
              hasAnyTaskWithDescription={hasAnyTaskWithDescription}
              isTaskLocked={isTaskLocked}
              rowContainsScheduledAssessment={rowContainsScheduledAssessment}
              tiles={tiles}
              task={task}
              isSequentiallyLocked={isSequentiallyLocked}
              fetchAssessmentLaunguages={fetchAssessmentLaunguages}
              showTileBanner={showTileBanner}
              handleAdvancedRRBtnClick={handleAdvancedRRBtnClick}
            />
          );
        })}
      </Grid>
    </>
  );
};

export default TaskTilesSection;
